import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import { ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { formatMoney } from '@/utils/format';
import { EpcEstimateBoqImportWizard } from './EpcEstimateBoqImportWizard';
import { EpcEstimateBoqAddDrawer } from './EpcEstimateBoqAddDrawer';

const { Text } = Typography;

export interface EpcEstimateBoqWorkbenchProps {
  estimateId: string;
  revisionId: string;
  readOnly: boolean;
  lines: any[];
  loading?: boolean;
  boqForm: FormInstance;
  onAdd: (values: Record<string, unknown>) => void;
  addPending?: boolean;
  onImported: () => void;
  /** Clear all Client BOQ lines on this revision (confirm handled by parent). */
  onClearBoq?: () => void;
  boqRollups?: Map<
    string,
    {
      line_count: number;
      total_amount: number;
      sell_amount?: number;
      margin_pct?: number | null;
      coverage_status?: string;
    }
  >;
  onBoqLineSelect?: (lineId: string | null) => void;
  selectedBoqLineId?: string | null;
  onShowLinkedCosts?: (boqLineId: string) => void;
  /** UX3.1 — add via drawer instead of inline form */
  useDrawerAdd?: boolean;
  /** UX3.2 — full-width immersive table (no Card, toolbar in command bar) */
  immersive?: boolean;
  /** Filter chips: all | without_cost | unlinked */
  coverageChipFilter?: 'all' | 'without_cost' | 'unlinked';
  onCoverageChipFilterChange?: (f: 'all' | 'without_cost' | 'unlinked') => void;
  onRowOpen?: (lineId: string) => void;
  importOpen?: boolean;
  onImportOpenChange?: (open: boolean) => void;
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
}

function parseMeta(raw: unknown): any {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return null;
  }
}

function buildTree(flat: any[]): any[] {
  const byId = new Map<string, any>();
  for (const row of flat) {
    byId.set(row.id, { ...row, key: row.id, children: [] as any[] });
  }
  const roots: any[] = [];
  for (const row of flat) {
    const node = byId.get(row.id)!;
    const parentId = row.parent_id || row.parentId;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const prune = (nodes: any[]): any[] =>
    nodes.map((n) => ({
      ...n,
      children: n.children?.length ? prune(n.children) : undefined,
    }));
  return prune(roots);
}

/**
 * EST3 UX2 Wave 2 — Client BOQ workbench (hierarchy + import + provenance).
 */
export const EpcEstimateBoqWorkbench: React.FC<EpcEstimateBoqWorkbenchProps> = ({
  estimateId,
  revisionId,
  readOnly,
  lines,
  loading,
  boqForm,
  onAdd,
  addPending,
  onImported,
  onClearBoq,
  boqRollups,
  onBoqLineSelect,
  selectedBoqLineId,
  onShowLinkedCosts,
  useDrawerAdd = true,
  immersive,
  coverageChipFilter = 'all',
  onCoverageChipFilterChange,
  onRowOpen,
  importOpen: importOpenProp,
  onImportOpenChange,
  addOpen: addOpenProp,
  onAddOpenChange,
}) => {
  const [importOpenLocal, setImportOpenLocal] = useState(false);
  const [addOpenLocal, setAddOpenLocal] = useState(false);
  const importOpen = importOpenProp ?? importOpenLocal;
  const setImportOpen = onImportOpenChange ?? setImportOpenLocal;
  const addOpen = addOpenProp ?? addOpenLocal;
  const setAddOpen = onAddOpenChange ?? setAddOpenLocal;
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    let rows = lines || [];
    const q = filter.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const hay = `${r.item_number || ''} ${r.description || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }
    if (immersive && coverageChipFilter === 'without_cost' && boqRollups) {
      rows = rows.filter((r) => {
        const s = boqRollups.get(r.id)?.coverage_status;
        return s === 'no_cost' || !boqRollups.has(r.id);
      });
    }
    return rows;
  }, [lines, filter, immersive, coverageChipFilter, boqRollups]);

  const treeData = useMemo(() => buildTree(filtered), [filtered]);

  const inner = (
    <>
    <Space direction="vertical" style={{ width: '100%' }} size={immersive ? 'small' : 'middle'}>
      <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Filter item / description"
            style={{ width: immersive ? 200 : 240 }}
            onSearch={setFilter}
            onChange={(e) => {
              if (!e.target.value) setFilter('');
            }}
          />
          {immersive && onCoverageChipFilterChange ? (
            <Segmented
              size="small"
              value={coverageChipFilter}
              onChange={(v) => onCoverageChipFilterChange(v as 'all' | 'without_cost' | 'unlinked')}
              options={[
                { label: 'All', value: 'all' },
                { label: 'Without cost', value: 'without_cost' },
              ]}
            />
          ) : null}
          <Text type="secondary">
            {lines?.length || 0} line(s)
            {selectedKeys.length ? ` · ${selectedKeys.length} selected` : ''}
          </Text>
        </Space>
        {!immersive ? (
          <Space wrap>
            <Button
              icon={<ImportOutlined />}
              disabled={readOnly}
              onClick={() => setImportOpen(true)}
            >
              Import Excel
            </Button>
            <Button danger disabled={readOnly || !(lines?.length)} onClick={onClearBoq}>
              Clear BOQ
            </Button>
            {!readOnly && useDrawerAdd ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                Add line
              </Button>
            ) : null}
          </Space>
        ) : null}
      </Space>

        {readOnly ? (
          <Alert type="info" showIcon message="Approved revision — BOQ is read-only" />
        ) : !useDrawerAdd ? (
          <Form form={boqForm} layout="inline" onFinish={onAdd} style={{ marginBottom: 0 }}>
            <Form.Item name="itemNumber">
              <Input placeholder="Item #" style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="description" rules={[{ required: true }]}>
              <Input placeholder="Description" style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="quantity" initialValue={1}>
              <InputNumber placeholder="Qty" min={0} />
            </Form.Item>
            <Form.Item name="unitRate" initialValue={0}>
              <InputNumber placeholder="Unit rate" min={0} />
            </Form.Item>
            <Form.Item name="parentId">
              <Select
                allowClear
                placeholder="Parent"
                style={{ width: 140 }}
                options={(lines || []).map((n: any) => ({
                  value: n.id,
                  label: n.item_number || n.description,
                }))}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={addPending}>
              Add
            </Button>
          </Form>
        ) : null}

        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={treeData}
          pagination={
            (filtered.length || 0) > 50 ? { pageSize: 50, showSizeChanger: true } : false
          }
          scroll={{ x: immersive ? 1100 : true }}
          expandable={{ defaultExpandAllRows: false }}
          onRow={(row) => ({
            onClick: () => {
              if (onRowOpen) {
                onRowOpen(row.id);
              } else if (onBoqLineSelect) {
                onBoqLineSelect(row.id);
              }
            },
            style: { cursor: onRowOpen || onBoqLineSelect ? 'pointer' : undefined },
          })}
          rowSelection={
            immersive && onRowOpen
              ? undefined
              : {
                  selectedRowKeys: selectedBoqLineId
                    ? [selectedBoqLineId]
                    : selectedKeys,
                  onChange: (keys) => {
                    setSelectedKeys(keys);
                    if (onBoqLineSelect) {
                      onBoqLineSelect(keys.length ? String(keys[keys.length - 1]) : null);
                    }
                  },
                  type: onBoqLineSelect ? 'radio' : 'checkbox',
                  checkStrictly: true,
                }
          }
          columns={[
            { title: 'Item', dataIndex: 'item_number', width: 110 },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Qty', dataIndex: 'quantity', width: 80 },
            {
              title: 'Rate',
              dataIndex: 'unit_rate',
              width: 100,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              width: 110,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            {
              title: 'Cost',
              width: 100,
              render: (_: unknown, row: any) => {
                const r = boqRollups?.get(row.id);
                return r
                  ? formatMoney(r.total_amount, { display: 'plain', maximumFractionDigits: 0 })
                  : '—';
              },
            },
            {
              title: 'Links',
              width: 56,
              render: (_: unknown, row: any) => boqRollups?.get(row.id)?.line_count ?? '—',
            },
            {
              title: 'Margin',
              width: 72,
              render: (_: unknown, row: any) => {
                const m = boqRollups?.get(row.id)?.margin_pct;
                return m != null ? `${m}%` : '—';
              },
            },
            {
              title: 'Cov',
              width: 72,
              render: (_: unknown, row: any) => {
                const s = boqRollups?.get(row.id)?.coverage_status;
                if (!s || s === 'linked') return <Tag color="green">OK</Tag>;
                if (s === 'no_cost') return <Tag color="orange">No cost</Tag>;
                return <Tag>{s}</Tag>;
              },
            },
            {
              title: '',
              width: 90,
              render: (_: unknown, row: any) =>
                onShowLinkedCosts && !onRowOpen ? (
                  <Button
                    size="small"
                    type="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowLinkedCosts(row.id);
                    }}
                  >
                    Costs
                  </Button>
                ) : null,
            },
            ...(immersive
              ? []
              : [
                  {
                    title: 'Provenance',
                    width: 200,
                    render: (_: unknown, row: any) => {
                      const meta = parseMeta(row.meta_json ?? row.metaJson);
                      const imp = meta?.import;
                      if (!imp) return <Text type="secondary">—</Text>;
                      const page =
                        imp.sourcePage != null
                          ? ` p.${imp.sourcePage}${
                              imp.sourcePageEnd && imp.sourcePageEnd !== imp.sourcePage
                                ? `-${imp.sourcePageEnd}`
                                : ''
                            }`
                          : '';
                      return (
                        <Space size={4} wrap>
                          <Tag color="blue" title={imp.fileName || undefined}>
                            {imp.source || 'import'}
                            {page} r{imp.row ?? '?'}
                          </Tag>
                          {imp.documentId ? (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const { documentService } = await import(
                                    '@/services/document.service'
                                  );
                                  await documentService.openDocumentInNewTab(String(imp.documentId));
                                } catch {
                                  /* ignore */
                                }
                              }}
                            >
                              PDF
                            </Button>
                          ) : null}
                        </Space>
                      );
                    },
                  },
                ]),
          ]}
        />
      </Space>

      <EpcEstimateBoqImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        estimateId={estimateId}
        revisionId={revisionId}
        readOnly={readOnly}
        onImported={onImported}
      />

      <EpcEstimateBoqAddDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        form={boqForm}
        readOnly={readOnly}
        lines={lines || []}
        onSubmit={onAdd}
        pending={addPending}
        defaultParentId={selectedBoqLineId}
      />
    </>
  );

  if (immersive) {
    return inner;
  }

  return (
    <Card size="small">
      {inner}
    </Card>
  );
};
